import { z } from 'zod';

const STORAGE_KEY = 'typingwhiz:games:v1';

const GameRecordSchema = z.object({
  bestScore: z.number().nonnegative(),
  bestWpm: z.number().nonnegative(),
  playCount: z.number().int().nonnegative(),
});

const StoreSchema = z.object({
  version: z.literal(1),
  games: z.record(z.string(), GameRecordSchema),
});

function getStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const t = '__tw_g_test__';
    window.localStorage.setItem(t, '1');
    window.localStorage.removeItem(t);
    return window.localStorage;
  } catch {
    return null;
  }
}

/** @returns {Record<string, { bestScore: number, bestWpm: number, playCount: number }>} */
export function readGameRecords() {
  const s = getStorage();
  if (!s) return {};
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = StoreSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) {
      s.removeItem(STORAGE_KEY);
      return {};
    }
    return parsed.data.games;
  } catch {
    return {};
  }
}

/**
 * @param {string} gameId - e.g., 'falling-words:easy'
 */
export function getGameRecord(gameId) {
  return readGameRecords()[gameId] || null;
}

/**
 * @param {{ gameId: string, score: number, wpm: number }} record
 */
export function recordGameAttempt({ gameId, score, wpm }) {
  const s = getStorage();
  if (!s) return null;
  const existing = readGameRecords();
  const prev = existing[gameId] || { bestScore: 0, bestWpm: 0, playCount: 0 };
  const updated = {
    bestScore: Math.max(prev.bestScore, Math.round(score)),
    bestWpm: Math.max(prev.bestWpm, Math.round(wpm)),
    playCount: prev.playCount + 1,
  };
  const next = { ...existing, [gameId]: updated };
  try {
    s.setItem(STORAGE_KEY, JSON.stringify({ version: 1, games: next }));
    return updated;
  } catch (e) {
    console.warn('Failed to save game record:', e.message);
    return null;
  }
}